import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { CATEGORY_ICONS, FILTER_KEYS } from "@/lib/categoryMeta";
import { Search, X, ChevronRight, Sparkles } from "lucide-react";

export default function Library() {
  const { t, lang } = useApp();
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [filter, setFilter] = useState("all");
  const [category, setCategory] = useState("");
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Astrology & Palmistry Q&A Library | AstroFriend";
    api.get("/qa/categories").then(({ data }) => setCategories(data)).catch(() => {});
  }, []);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(query), 300);
    return () => clearTimeout(id);
  }, [query]);

  const fetchItems = useCallback(() => {
    setLoading(true);
    const params = { filter, q: debounced };
    if (category) params.category = category;
    api.get("/qa/list", { params })
      .then(({ data }) => setItems(data))
      .finally(() => setLoading(false));
  }, [filter, debounced, category]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const pickCat = (c) => (lang === "hi" ? c.hi : c.en);
  const activeCat = categories.find((c) => c.slug === category);

  return (
    <div className="mx-auto max-w-6xl px-6 py-12">
      <div className="text-center mb-8 rise-in">
        <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-amber-50">{t.library.title}</h1>
        <p className="mt-3 text-slate-300 max-w-2xl mx-auto">{t.library.subtitle}</p>
      </div>

      {/* Search */}
      <div className="relative max-w-2xl mx-auto mb-6">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-amber-300/70" size={20} />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t.library.searchPlaceholder}
          data-testid="qa-search-input"
          className="w-full glass rounded-full pl-12 pr-12 py-4 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-amber-400/60 border border-amber-500/25"
        />
        {query && (
          <button onClick={() => setQuery("")} data-testid="qa-search-clear" className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-amber-300">
            <X size={18} />
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 justify-center mb-10">
        {FILTER_KEYS.map((f) => (
          <button
            key={f}
            onClick={() => { setFilter(f); setCategory(""); }}
            data-testid={`filter-${f}`}
            className={`rounded-full px-4 py-1.5 text-sm transition-all ${
              filter === f && !category ? "btn-gold" : "glass text-slate-300 hover:text-amber-200"
            }`}
          >
            {t.library.filters[f]}
          </button>
        ))}
      </div>

      {/* Categories */}
      {!category && !debounced && (
        <div className="mb-12">
          <h2 className="font-serif text-xl text-amber-200 mb-5">{t.library.categoriesTitle}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {categories.map((c) => {
              const Icon = CATEGORY_ICONS[c.icon] || Sparkles;
              return (
                <button
                  key={c.slug}
                  onClick={() => { setCategory(c.slug); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                  data-testid={`category-${c.slug}`}
                  className="glass rounded-2xl p-5 text-left hover:scale-[1.03] transition-transform group"
                >
                  <div className="h-11 w-11 rounded-xl grid place-items-center bg-gradient-to-br from-amber-500/20 to-purple-600/20 border border-amber-500/30 mb-3 group-hover:gold-glow">
                    <Icon className="text-amber-300" size={20} />
                  </div>
                  <div className="font-medium text-slate-100 leading-snug">{pickCat(c)}</div>
                  <div className="text-xs text-amber-300/60 mt-1">{c.count} {t.library.questions}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Results */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-serif text-xl text-amber-200">
          {activeCat ? pickCat(activeCat) : t.library.allQuestions}
        </h2>
        {category && (
          <button onClick={() => setCategory("")} data-testid="clear-category" className="text-sm text-amber-300/80 hover:text-amber-200 flex items-center gap-1">
            <X size={14} /> {t.library.clearCategory}
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-16 text-amber-200/70">{t.common.loading}</div>
      ) : items.length === 0 ? (
        <div className="glass rounded-2xl p-12 text-center text-slate-300" data-testid="qa-no-results">{t.library.noResults}</div>
      ) : (
        <div className="grid gap-3">
          {items.map((q) => (
            <Link
              key={q.qid}
              to={`/library/${q.qid}`}
              data-testid={`qa-item-${q.qid}`}
              className="glass rounded-2xl p-5 flex items-start gap-4 hover:scale-[1.01] transition-transform"
            >
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-slate-100">{lang === "hi" ? q.question_hi : q.question_en}</h3>
                <p className="text-sm text-slate-400 mt-1 line-clamp-2">{lang === "hi" ? q.short_hi : q.short_en}</p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {(q.keywords || []).slice(0, 3).map((k) => (
                    <span key={k} className="text-[11px] px-2 py-0.5 rounded-full bg-purple-800/40 text-purple-200 border border-purple-500/20">{k}</span>
                  ))}
                </div>
              </div>
              <ChevronRight className="text-slate-500 shrink-0 mt-1" size={20} />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
